import type { CartFragment } from '../../graphql/magento/generated'
import { fnv1a } from '../../utils/hash'

const FIELD_SEPARATOR = '\u001f'
const SUBFIELD_SEPARATOR = '\u001e'

type CartAddress = Pick<
  NonNullable<CartFragment['billing_address']>,
  'country' | 'region' | 'postcode' | 'city' | 'street'
>

// The fragment selects no address id, so the fields that move shipping and tax stand in for one.
const addressKey = (address: CartAddress | null | undefined): string =>
  address
    ? [
        address.country.code,
        address.region?.code ?? '',
        address.postcode ?? '',
        address.city,
        ...address.street.map((line) => line ?? ''),
      ].join(SUBFIELD_SEPARATOR)
    : ''

// Each list is prefixed with its length so adjacent lists cannot shift fields between them.
const list = (values: readonly string[]): readonly string[] => [String(values.length), ...values]

/**
 * A stand-in for the `updated_at` the cart fragment lacks: two tabs holding the same server cart
 * compute the same value.
 */
export const cartFingerprint = (cart: CartFragment): string =>
  fnv1a(
    [
      cart.id,
      String(cart.total_quantity),
      ...list(
        (cart.items ?? []).flatMap((item) => (item ? [item.id, String(item.quantity)] : ['', ''])),
      ),
      ...list((cart.applied_coupons ?? []).map((coupon) => coupon?.code ?? '')),
      String(cart.prices?.grand_total?.value ?? ''),
      ...list((cart.shipping_addresses ?? []).map(addressKey)),
      addressKey(cart.billing_address),
      cart.selected_payment_method?.code ?? '',
      cart.practitioner_code ?? '',
      cart.dispensary_promo_code ?? '',
    ].join(FIELD_SEPARATOR),
  )
